import React, { useEffect, useState, useMemo } from 'react';
import { CardTitle } from '../../components/Card';
import { DataTable, ColumnConfig } from '../../components/Table';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import { Info, Loader2, Wrench } from 'lucide-react';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { getSeverityColor } from '../../common/Utils/color';
import { useVulnerabilityService } from '../../components/services/vulnerabilityService';
import { useCreateRemediationPlan } from '../../components/services/createRemediationPlan';

import { getPatchifiBanner, getPatchifiVulnerabilities } from './Products/patchifiLogic';
import {
  getSentinelOneBanner as getSentinelOneBannerData,
  getSentinelOneVulnerabilities,
  handleSentinelOneRemediation,
} from './Products/sentinelOneLogic';
import { fetchOsUpdateVulnerabilities, fetchOsUpdateVulnerabilitiesBanner } from './Products/osVulnerabilityLogic';
import { Vulnerability, PaginationInfo, VulnerabilityStats, RemediationSummary, RemediationItemProgress } from './Utils/types';
import { TwoLabelToggle } from '../../components/Toggle';
import RemediationProgressModal from '../../components/Modal/RemediationProgressModal';
import { ENABLE_REMEDIATION_PROGRESS_MODAL } from '../../common/config/featureFlags';
import { usePermissions } from '../../hooks/usePermissions';
import { useRemediationJob } from '../../providers/RemediationJobProvider';

const itemsPerPage = 20;

interface VulnerabilitiesTableProps {
  source?: string;
}

const getVulnerabilityRowKey = (
  item: {
    softwareAssetInventoryId?: string;
    cveId?: string;
    packageIdentifier?: string;
    version?: string;
    title?: string;
    cveUrl?: string;
  },
  source: string,
  page: number,
  index: number,
) => {
  const compositeKey = [source, item.softwareAssetInventoryId, item.cveId, item.packageIdentifier, item.version, item.title, item.cveUrl]
    .filter(Boolean)
    .join('::');

  return compositeKey || `${source}::page-${page}::row-${index}`;
};

const hasPackageIdentifier = (vulnerability: Pick<Vulnerability, 'packageIdentifier'>) => !!vulnerability.packageIdentifier?.trim();

const getKevDisplay = (value: Vulnerability['kev']) => {
  if (value === true) {
    return { label: 'Yes', color: '#10b981', fontWeight: '600' as const };
  }

  if (value === false) {
    return { label: 'No', color: '#6b7280' };
  }

  if (value === null || value === undefined || value === '') {
    return { label: '-', color: '#6b7280' };
  }

  return { label: String(value), color: '#d1d5db', fontWeight: '600' as const };
};

const VulnerabilitiesTable: React.FC<VulnerabilitiesTableProps> = ({ source = 'Patchifi' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const auth = useAuth();
  const { canEdit } = usePermissions();
  const canRemediate = canEdit('remediationPlan');
  const { selectedTenantId } = useTenantSelection();
  const isSentinelOne = source === 'SentinelOne';

  // Get search from URL params
  const queryParams = new URLSearchParams(location.search);
  const searchTerm = queryParams.get('search') || '';

  const [searchInput, setSearchInput] = useState<string>(searchTerm);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([]);
  const [stats, setStats] = useState<VulnerabilityStats>({
    totalVulnerabilities: 0,
    totalKEV: 0,
    totalCritical: 0,
    totalEPSSAboveThreshold: 0,
    affectedDevices: 0,
  });
  const {
    getVulnerabilityBanner,
    getSoftwareAssets,
    getSentinelOneSoftwareVulnerabilities,
    getSentinelOneBanner,
    getManagedPackageByIdentifier,
  } = useVulnerabilityService();
  const { createRemediationPlan } = useCreateRemediationPlan();
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');
  const [selectedKev, setSelectedKev] = useState<string>('Any');
  const [selectedEpss, setSelectedEpss] = useState<string>('Any');
  const [selectedVulnerabilities, setSelectedVulnerabilities] = useState<Record<string, Vulnerability>>({});
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [remediationSummary, setRemediationSummary] = useState<RemediationSummary | null>(null);
  const [showOsVulnerabilities, setShowOsVulnerabilities] = useState<boolean>(false);
  const [progressModalOpen, setProgressModalOpen] = useState<boolean>(false);
  const [progressItems, setProgressItems] = useState<RemediationItemProgress[]>([]);
  const [progressComplete, setProgressComplete] = useState<boolean>(false);
  const [sentinelOneRunning, setSentinelOneRunning] = useState<boolean>(false);
  const { isRemediationRunning, startRemediation } = useRemediationJob();
  const isRemediating = isRemediationRunning || sentinelOneRunning;

  useEffect(() => {
    setSelectedVulnerabilities((prev) => {
      const nextEntries = Object.entries(prev).filter(([, vulnerability]) => hasPackageIdentifier(vulnerability));
      return nextEntries.length === Object.keys(prev).length ? prev : Object.fromEntries(nextEntries);
    });
  }, []);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedVulnerabilities({});
    setExpandedRowId(null);
    setSelectedKev('Any');
    setRemediationSummary(null);
  }, [source, showOsVulnerabilities]);

  // Check if all current page items are selected
  const isAllCurrentPageSelected = useMemo(() => {
    const selectableVulnerabilities = vulnerabilities.filter(hasPackageIdentifier);

    if (selectableVulnerabilities.length === 0) return false;

    return selectableVulnerabilities.every((vuln) => {
      const key = vuln.rowKey;
      return !!selectedVulnerabilities[key];
    });
  }, [vulnerabilities, selectedVulnerabilities]);

  const hasSelectableVulnerabilities = useMemo(() => vulnerabilities.some(hasPackageIdentifier), [vulnerabilities]);

  // Fetch banner data
  useEffect(() => {
    if (auth && auth.userData && selectedTenantId) {
      getBanner();
    }
  }, [auth, selectedTenantId, isSentinelOne, showOsVulnerabilities, source]);

  // Fetch vulnerabilities table data
  useEffect(() => {
    getVulnerabilities(currentPage);
  }, [selectedTenantId, currentPage, selectedSeverity, selectedKev, selectedEpss, searchTerm, source, showOsVulnerabilities]);

  const getBanner = async () => {
    try {
      if (auth && auth.userData) {
        const response = showOsVulnerabilities
          ? await fetchOsUpdateVulnerabilitiesBanner({
              tenantId: selectedTenantId as string,
              source,
            })
          : isSentinelOne
            ? await getSentinelOneBannerData(selectedTenantId as string, getSentinelOneBanner)
            : await getPatchifiBanner(selectedTenantId as string, getVulnerabilityBanner);

        if (response?.isSuccess && response?.data) {
          setStats(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching vulnerability banner:', error);
    }
  };

  const totalPages = paginationInfo?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const applySearch = () => {
    const value = searchInput;
    const params = new URLSearchParams(location.search);

    if (value.trim()) {
      params.set('search', value.trim());
    } else {
      params.delete('search');
    }

    params.delete('page');
    setCurrentPage(1);

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applySearch();
    }
  };

  const getAgeInDays = (publishedDateValue?: string) => {
    if (!publishedDateValue) return 0;
    const publishedDate = new Date(publishedDateValue);
    if (Number.isNaN(publishedDate.getTime())) return 0;
    const today = new Date();
    return Math.floor((today.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const toDisplaySeverity = (severity?: string) => {
    if (!severity) return 'Unknown';
    const normalized = severity.toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getVulnerabilities = async (page = 1) => {
    try {
      setTableLoading(true);

      if (!isSentinelOne && !selectedTenantId) {
        setVulnerabilities([]);
        setPaginationInfo(null);
        return;
      }

      if (showOsVulnerabilities) {
        if (!selectedTenantId) {
          setVulnerabilities([]);
          setPaginationInfo(null);
          return;
        }

        const result = await fetchOsUpdateVulnerabilities({
          tenantId: selectedTenantId,
          source,
          severity: selectedSeverity,
          pageNumber: page,
          pageSize: itemsPerPage,
          search: searchTerm,
        });

        setVulnerabilities(result.vulnerabilities);
        setPaginationInfo(result.paginationInfo);
        return;
      }

      if (isSentinelOne) {
        const result = await getSentinelOneVulnerabilities({
          selectedTenantId: selectedTenantId as string,
          page,
          itemsPerPage,
          selectedSeverity,
          selectedKev,
          selectedEpss,
          searchTerm,
          getSentinelOneSoftwareVulnerabilities,
        });
        setVulnerabilities(result.vulnerabilities);
        setPaginationInfo(result.paginationInfo);
        if (result.stats) setStats(result.stats);
      } else {
        const result = await getPatchifiVulnerabilities({
          selectedTenantId: selectedTenantId as string,
          page,
          itemsPerPage,
          selectedSeverity,
          selectedKev,
          selectedEpss,
          searchTerm,
          getSoftwareAssets,
        });
        setVulnerabilities(result.vulnerabilities);
        setPaginationInfo(result.paginationInfo);
      }
    } catch (error) {
      console.error('Error fetching vulnerabilities:', error);
      setVulnerabilities([]);
      setPaginationInfo(null);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSeverityFilter = (severity: string) => {
    setSelectedSeverity(selectedSeverity === severity ? '' : severity);
    setCurrentPage(1);
  };

  const toggleVulnerabilitySelection = (vuln: Vulnerability) => {
    if (!hasPackageIdentifier(vuln)) {
      return;
    }

    const key = vuln.rowKey;
    setSelectedVulnerabilities((prev) => {
      const updated = { ...prev };
      if (updated[key]) {
        delete updated[key];
      } else {
        updated[key] = vuln;
      }
      return updated;
    });
  };

  const toggleSelectAll = () => {
    const selectableVulnerabilities = vulnerabilities.filter(hasPackageIdentifier);

    if (selectableVulnerabilities.length === 0) {
      return;
    }

    if (isAllCurrentPageSelected) {
      // Deselect all items on current page
      setSelectedVulnerabilities((prev) => {
        const updated = { ...prev };
        selectableVulnerabilities.forEach((vuln) => {
          const key = vuln.rowKey;
          delete updated[key];
        });
        return updated;
      });
    } else {
      // Select all items on current page
      setSelectedVulnerabilities((prev) => {
        const updated = { ...prev };
        selectableVulnerabilities.forEach((vuln) => {
          const key = vuln.rowKey;
          updated[key] = vuln;
        });
        return updated;
      });
    }
  };

  const getSeverityBadge = (severity: string) => {
    const baseClasses = 'px-3 py-1 rounded-md text-xs font-semibold inline-block border';
    const colorClasses = getSeverityColor(severity);
    return `${baseClasses} ${colorClasses}`;
  };

  const handleViewDetails = (rowKey: string) => {
    setExpandedRowId(expandedRowId === rowKey ? null : rowKey);
  };

  const buildSummaryReasons = (reasonCounts: Record<string, number>, includeRequestFailure: boolean) => {
    const reasons: string[] = [];

    if (reasonCounts.missingAgentId > 0) {
      reasons.push(`Missing agentId: ${reasonCounts.missingAgentId}`);
    }
    if (reasonCounts.missingFixVersion > 0) {
      reasons.push(`Missing latest version: ${reasonCounts.missingFixVersion}`);
    }
    if (reasonCounts.missingCurrentVersion > 0) {
      reasons.push(`Missing current version: ${reasonCounts.missingCurrentVersion}`);
    }
    if (reasonCounts.alreadyLatestVersion > 0) {
      reasons.push(`Already latest version: ${reasonCounts.alreadyLatestVersion}`);
    }
    if (reasonCounts.missingPackageIdentifier > 0) {
      reasons.push(`Missing packageIdentifier: ${reasonCounts.missingPackageIdentifier}`);
    }
    if (includeRequestFailure) {
      reasons.push('Create remediation plan request failed');
    }

    return reasons;
  };

  const handleRemediateSelected = async () => {
    const selectedCount = Object.keys(selectedVulnerabilities).length;
    if (selectedCount === 0) {
      addToast('Please select vulnerabilities to remediate', 'warning', 3000, 'Warning');
      return;
    }

    if (!selectedTenantId) {
      addToast('No tenant selected', 'error', 3000);
      return;
    }

    if (isRemediating) {
      return;
    }

    // Runs in the background across pages; the provider asks the user to review before creating.
    if (!isSentinelOne) {
      startRemediation({
        kind: showOsVulnerabilities ? 'os' : 'software',
        tenantId: selectedTenantId,
        vulnerabilities: Object.values(selectedVulnerabilities),
      });
      setSelectedVulnerabilities({});
      return;
    }

    // Seed the progress modal with every selected vulnerability as "pending" so the user sees
    // the full worklist immediately, then fill in stages as onProgress fires.
    let onProgress: ((update: RemediationItemProgress) => void) | undefined;
    if (ENABLE_REMEDIATION_PROGRESS_MODAL) {
      const initialItems: RemediationItemProgress[] = Object.values(selectedVulnerabilities).map((vulnerability) => ({
        key: vulnerability.rowKey,
        title: vulnerability.title || vulnerability.cveId || 'Unknown vulnerability',
        cveId: vulnerability.cveId,
        stage: 'pending',
      }));
      setProgressItems(initialItems);
      setProgressComplete(false);
      setProgressModalOpen(true);

      onProgress = (update: RemediationItemProgress) => {
        setProgressItems((prev) => prev.map((item) => (item.key === update.key ? { ...item, ...update } : item)));
      };
    }

    setSentinelOneRunning(true);
    try {
      const result = await handleSentinelOneRemediation({
        selectedVulnerabilities,
        selectedTenantId,
        addToast: addToast as any,
        getManagedPackageByIdentifier,
        createRemediationPlan,
        onProgress,
      });
      setRemediationSummary(result.remediationSummary);
      if (ENABLE_REMEDIATION_PROGRESS_MODAL) setProgressComplete(true);
      if (result.clearSelection) {
        setSelectedVulnerabilities({});
      }
    } finally {
      setSentinelOneRunning(false);
    }
  };

  const selectedCount = Object.keys(selectedVulnerabilities).length;
  const canClickRemediate = selectedCount > 0 && canRemediate && !isRemediating;
  let remediateLabel = 'Remediate';
  if (isRemediating) remediateLabel = 'Remediation in progress…';
  else if (selectedCount > 0) remediateLabel = `Remediate (${selectedCount})`;

  // Define table columns
  const columns: ColumnConfig[] = [
    {
      key: 'checkbox',
      header: '',
      width: '50px',
      align: 'center',
      renderHeader: () => (
        <div
          onClick={() => {
            if (hasSelectableVulnerabilities) {
              toggleSelectAll();
            }
          }}
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            border: '2px solid #888',
            backgroundColor: isAllCurrentPageSelected && hasSelectableVulnerabilities ? '#00FF00' : 'transparent',
            cursor: hasSelectableVulnerabilities ? 'pointer' : 'not-allowed',
            opacity: hasSelectableVulnerabilities ? 1 : 0.4,
            display: 'inline-block',
            position: 'relative',
          }}
          title={
            hasSelectableVulnerabilities
              ? isAllCurrentPageSelected
                ? 'Deselect all selectable rows on this page'
                : 'Select all selectable rows on this page'
              : 'No selectable rows on this page'
          }
        ></div>
      ),
      render: (_, item) => {
        const isSelectable = hasPackageIdentifier(item);
        const isSelected = !!selectedVulnerabilities[item.rowKey];
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (isSelectable) {
                toggleVulnerabilitySelection(item);
              }
            }}
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              border: '2px solid #888',
              backgroundColor: isSelected ? '#00FF00' : 'transparent',
              cursor: isSelectable ? 'pointer' : 'not-allowed',
              opacity: isSelectable ? 1 : 0.4,
              display: 'inline-block',
              position: 'relative',
            }}
            title={isSelectable ? (isSelected ? 'Deselect' : 'Select') : 'Cannot select rows without package identifier'}
          ></div>
        );
      },
    },
    {
      key: 'title',
      header: 'TITLE',
      width: '2fr',
      render: (value, item) => {
        const isOs = showOsVulnerabilities;
        const isClickable = isOs ? selectedTenantId && item.kbId : item.softwareAssetInventoryId;
        const handleClick = () => {
          if (isOs && selectedTenantId && item.kbId) {
            const encodedTitle = encodeURIComponent(value);
            navigate(`/patchCenter/DeviceNeedingPatch/${selectedTenantId}?type=osUpdate&kbid=${item.kbId}&title=${encodedTitle}`, {
              state: { ...item },
            });
          } else if (!isOs && item.softwareAssetInventoryId) {
            navigate(`/SoftwareAssets/info/${item.softwareAssetInventoryId}`, { state: { ...item } });
          }
        };
        return (
          <span
            style={{
              color: '#d1d5db',
              cursor: isClickable ? 'pointer' : 'default',
              textDecoration: isClickable ? 'underline' : 'none',
            }}
            onClick={isClickable ? handleClick : undefined}
          >
            {value}
          </span>
        );
      },
    },
    {
      key: 'version',
      header: 'VERSION',
      width: '1fr',
      render: (value) => <span style={{ color: '#d1d5db' }}>{value || 'N/A'}</span>,
    },
    {
      key: 'cveId',
      header: 'CVE',
      width: '1fr',
      render: (value, item) => {
        if (item.cveUrl) {
          return (
            <a
              href={item.cveUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: '500', textDecoration: 'none', cursor: 'pointer' }}
              className="text-white hover:underline hover:text-blue-400 transition-colors duration-200"
            >
              {value}
            </a>
          );
        }
        return <span style={{ fontWeight: '500', color: '#ffffff' }}>{value}</span>;
      },
    },
    {
      key: 'severity',
      header: 'SEVERITY',
      width: '1fr',
      render: (value) => <span className={getSeverityBadge(value)}>{value}</span>,
    },
    {
      key: 'cvssScore',
      header: 'CVSS',
      width: '0.8fr',
      align: 'center',
      render: (value) => <span style={{ fontWeight: '600', color: '#ffffff' }}>{value}</span>,
    },
    {
      key: 'epssScore',
      header: 'EPSS',
      width: '0.8fr',
      align: 'center',
      render: (value) => {
        const formattedValue = typeof value === 'number' && value > 0 ? value.toFixed(5) : '0.00000';
        return <span style={{ fontWeight: '600', color: '#ffffff' }}>{formattedValue}</span>;
      },
    },
    {
      key: 'kev',
      header: 'KEV',
      width: '1fr',
      align: 'center',
      render: (value) => {
        const kevDisplay = getKevDisplay(value);
        return <span style={{ color: kevDisplay.color, fontWeight: kevDisplay.fontWeight }}>{kevDisplay.label}</span>;
      },
    },
    {
      key: 'affectedDevicesCount',
      header: 'AFFECTED',
      width: '1fr',
      align: 'center',
      render: (value) => <span style={{ color: '#d1d5db' }}>{value}</span>,
    },
    {
      key: 'ageInDays',
      header: 'AGE',
      width: '0.8fr',
      align: 'center',
      render: (value) => <span style={{ color: '#d1d5db' }}>{value}d</span>,
    },
    {
      key: 'actions',
      header: 'ACTIONS',
      width: '1fr',
      render: (_, item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetails(item.rowKey);
          }}
          className="bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
        >
          <Info size={14} />
          Details
        </button>
      ),
    },
  ];

  const renderExpandedContent = (item: Vulnerability) => (
    <div className="py-2">
      <div className="text-sm font-medium text-gray-400 mb-2">Description:</div>
      <div className="text-sm text-gray-300 whitespace-pre-wrap break-words">{item.description || 'No description available'}</div>
    </div>
  );

  return (
    <div style={{ backgroundColor: '#050505' }} className="p-6 text-white min-h-screen">
      {/* Top Cards - Banner */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Total Vulns', value: stats.totalVulnerabilities },
          { label: 'KEV (Exploited)', value: stats.totalKEV },
          { label: 'Critical', value: stats.totalCritical },
          { label: 'EPSS > 0.5', value: stats.totalEPSSAboveThreshold },
          { label: 'Affected Devices', value: stats.affectedDevices },
        ]?.map((card, i) => (
          <div
            key={i}
            style={{ backgroundColor: i % 2 === 0 ? '#2E2D2D' : '#1D1D1D' }}
            className="h-[120px] p-4 rounded-lg shadow flex flex-col br-20"
          >
            <CardTitle title={card.label} />
            <span className="text-2xl font-semibold mt-[25px] ml-[28px]">{card.value}</span>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex justify-between items-center mb-4">
        {/* Search on the left */}
        <div className="relative w-[350px]">
          <input
            type="text"
            placeholder="Search CVE / product / title"
            value={searchInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            className="bg-zinc-900 text-white px-4 py-2 rounded w-full border border-zinc-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 pr-10"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('');
                const params = new URLSearchParams(location.search);
                params.delete('search');
                params.delete('page');
                setCurrentPage(1);
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
            >
              ✕
            </button>
          )}
        </div>

        {/* Severity Filters Group */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <span className="text-gray-400 text-xs font-medium mr-1">Severity:</span>
          <button
            onClick={() => handleSeverityFilter('Critical')}
            className={`px-3 py-1 rounded text-xs font-medium transition-all bg-red-900 text-white ${
              selectedSeverity === 'Critical' ? 'ring-2 ring-white shadow-lg scale-105' : 'opacity-70 hover:opacity-100'
            }`}
          >
            Critical
          </button>
          <button
            onClick={() => handleSeverityFilter('High')}
            className={`px-3 py-1 rounded text-xs font-medium transition-all bg-red-500 text-white ${
              selectedSeverity === 'High' ? 'ring-2 ring-white shadow-lg scale-105' : 'opacity-70 hover:opacity-100'
            }`}
          >
            High
          </button>
          <button
            onClick={() => handleSeverityFilter('Medium')}
            className={`px-3 py-1 rounded text-xs font-medium transition-all bg-yellow-600 text-white ${
              selectedSeverity === 'Medium' ? 'ring-2 ring-white shadow-lg scale-105' : 'opacity-70 hover:opacity-100'
            }`}
          >
            Medium
          </button>
          <button
            onClick={() => handleSeverityFilter('Low')}
            className={`px-3 py-1 rounded text-xs font-medium transition-all bg-green-600 text-white ${
              selectedSeverity === 'Low' ? 'ring-2 ring-white shadow-lg scale-105' : 'opacity-70 hover:opacity-100'
            }`}
          >
            Low
          </button>
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-zinc-700"></div>

        {/* KEV Filter */}
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-xs font-medium">KEV:</span>
          <select
            value={selectedKev}
            onChange={(e) => {
              setSelectedKev(e.target.value);
              setCurrentPage(1);
            }}
            disabled={isSentinelOne || showOsVulnerabilities}
            className="bg-zinc-800 text-white px-3 py-1.5 rounded text-xs border border-zinc-700 hover:border-zinc-600 focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="Any">Any</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        {/* EPSS Filter */}
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-xs font-medium">EPSS:</span>
          <select
            value={selectedEpss}
            onChange={(e) => {
              setSelectedEpss(e.target.value);
              setCurrentPage(1);
            }}
            disabled={showOsVulnerabilities}
            className="bg-zinc-800 text-white px-3 py-1.5 rounded text-xs border border-zinc-700 hover:border-zinc-600 focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all cursor-pointer"
          >
            <option value="Any">Any</option>
            <option value=">0.5">&gt; 0.5</option>
            <option value=">0.7">&gt; 0.7</option>
          </select>
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-zinc-700"></div>
        {/* Filters and Remediate Button on the right */}
        <div className="flex items-center gap-4">
          <TwoLabelToggle
            checked={showOsVulnerabilities}
            leftLabel="Software"
            rightLabel="OS"
            onChange={(nextValue) => {
              setShowOsVulnerabilities(nextValue);
              setCurrentPage(1);
            }}
          />

          {/* Divider */}
          <div className="h-8 w-px bg-zinc-700"></div>

          {/* Remediate Button */}
          <button
            onClick={handleRemediateSelected}
            disabled={!canClickRemediate}
            title={isRemediating ? 'Please wait until the current remediation has finished' : undefined}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-all shadow-sm"
            style={{
              backgroundColor: canClickRemediate ? '#DC2626' : '#555',
              color: '#ffffff',
              cursor: canClickRemediate ? 'pointer' : 'not-allowed',
              opacity: canClickRemediate ? 1 : 0.6,
            }}
          >
            {isRemediating ? <Loader2 size={16} className="animate-spin" /> : <Wrench size={16} />}
            {remediateLabel}
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={vulnerabilities}
        loading={tableLoading}
        alternateRowColors={true}
        hoverEffect={true}
        headerStyle={{ backgroundColor: '#161617' }}
        rowStyle={{ padding: '1.25rem 1.5rem' }}
        containerStyle={{ background: '#1e1e1e', borderRadius: '8px' }}
        expandedRowId={expandedRowId}
        getRowId={(item) => item.rowKey}
        renderExpandedContent={renderExpandedContent}
      />

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} loading={tableLoading} onPageChange={handlePageChange} />

      {/* Stage-by-stage remediation progress */}
      {ENABLE_REMEDIATION_PROGRESS_MODAL && (
        <RemediationProgressModal
          show={progressModalOpen}
          onClose={() => setProgressModalOpen(false)}
          items={progressItems}
          isComplete={progressComplete}
        />
      )}
    </div>
  );
};

export default VulnerabilitiesTable;
